// src/layouts/admin/recordings/index.js
import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import RecordingsDataTable from "./data/RecordingsDataTable";
import AddRecording from "./modals/AddRecording";
import EditRecording from "./modals/EditRecording";
import DeleteRecording from "./modals/DeleteRecording";
import { getAllRecordings } from "services/recordingService";
import { useTableSelectionModals } from "hooks/useTableSelectionModals";

function ManageRecordings() {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState({ page: 1, limit: 25, total: 0 });
  const [openAdd, setOpenAdd] = useState(false);

  const {
    selectedItem: selectedRecording,
    openEdit,
    openDelete,
    handleEdit,
    handleDelete,
    handleCloseDelete,
    handleCloseEdit,
    onPaginationChange,
  } = useTableSelectionModals();

  const fetchRecordings = async (opts = {}) => {
    try {
      setLoading(true);
      const resp = await getAllRecordings({
        page: opts.page || pageInfo.page,
        limit: opts.limit || pageInfo.limit,
      });
      setRecordings(resp.data || []);
      setPageInfo({
        page: resp.page || 1,
        limit: resp.limit || 25,
        total: resp.total_records || 0,
      });
    } catch (err) {
      console.error("Failed to load recordings:", err);
      setRecordings([]);
      setPageInfo((p) => ({ ...p, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordings();
  }, []);

  const { columns, rows } = RecordingsDataTable({
    recordings,
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6">
                Sample Recordings {loading ? "(loading…)" : ""}
              </MDTypography>
              <MDBox display="flex" justifyContent="end" alignItems="center" gap="10px">
                <MDButton variant="gradient" color="info" onClick={() => setOpenAdd(true)}>
                  <Icon>add</Icon>&nbsp;Add Recording
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox pt={3} pb={3}>
              <Card
                sx={{
                  boxShadow: 4,
                  borderRadius: 2,
                  overflow: "visible",
                  backgroundColor: "#f9fafe",
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDBox>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted
                    entriesPerPage={{ defaultValue: pageInfo.limit, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries
                    canSearch
                    pagination={{ variant: "gradient", color: "info" }}
                    noEndBorder={false}
                    onPaginationChange={onPaginationChange}
                  />
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      <AddRecording
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSuccess={() => {
          setOpenAdd(false);
          fetchRecordings();
        }}
      />

      <EditRecording
        open={openEdit}
        onClose={handleCloseEdit}
        recording={selectedRecording}
        onSuccess={fetchRecordings}
      />
      <DeleteRecording
        key={selectedRecording?.id ?? "closed"}
        open={openDelete}
        onClose={handleCloseDelete}
        recording={selectedRecording}
        onSuccess={fetchRecordings}
      />
    </DashboardLayout>
  );
}

export default ManageRecordings;
